import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMessage } from "../../lib/api";
import { t } from "../../lib/i18n";
import { useAuth } from "../../context/AuthContext";
import { Loader } from "../../components/Blocks";

const ROLES = ["super_admin", "admin", "user", "partner_manager"];
const TYPES = [
  "member", "ambassador", "creator", "influencer", "model", "talent",
  "photographer", "videographer", "filmmaker", "event_personality", "production_partner",
];

export default function AdminPeople() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", display_name: "", security_role: "user", profile_types: ["member"] });

  const load = async () => setUsers((await api.get("/admin/users")).data);
  useEffect(() => {
    load().catch((e) => toast.error(errMessage(e)));
  }, []);

  const patch = async (id, payload) => {
    try {
      await api.patch(`/admin/users/${id}`, payload);
      toast.success("Utilisateur mis à jour");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success("Utilisateur créé");
      setCreating(false);
      setForm({ email: "", password: "", display_name: "", security_role: "user", profile_types: ["member"] });
      load();
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  if (users === null) return <Loader />;

  return (
    <div data-testid="admin-people">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="vb-display text-3xl lg:text-4xl font-bold">Personnes</h1>
        {me?.security_role === "super_admin" && (
          <button className="vb-btn vb-btn-primary !py-2" data-testid="admin-people-create-toggle" onClick={() => setCreating((v) => !v)}>
            {creating ? "Fermer" : "Nouvel utilisateur"}
          </button>
        )}
      </div>
      <p className="mt-3 text-sm text-neutral-500">
        Le rôle de sécurité gouverne les permissions. Les types de profil sont des identités
        multiples et n'accordent aucun privilège.
      </p>

      {creating && (
        <form onSubmit={create} className="mt-8 vb-card p-6 grid md:grid-cols-2 gap-6 max-w-3xl" data-testid="admin-people-create-form">
          <div>
            <label className="vb-label">Courriel</label>
            <input className="vb-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-people-email" />
          </div>
          <div>
            <label className="vb-label">Mot de passe (min. 8)</label>
            <input className="vb-input" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-people-password" />
          </div>
          <div>
            <label className="vb-label">Nom affiché</label>
            <input className="vb-input" required value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="admin-people-name" />
          </div>
          <div>
            <label className="vb-label">Rôle de sécurité</label>
            <select className="vb-input" value={form.security_role} onChange={(e) => setForm({ ...form, security_role: e.target.value })} data-testid="admin-people-role">
              {ROLES.map((r) => (
                <option key={r} value={r} className="bg-black">
                  {r}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="vb-label">Types de profil</label>
            <div className="flex flex-wrap gap-2 mt-3">
              {TYPES.map((tp) => (
                <button
                  key={tp}
                  type="button"
                  className="vb-chip"
                  data-active={form.profile_types.includes(tp)}
                  data-testid={`admin-people-type-${tp}`}
                  onClick={() =>
                    setForm({
                      ...form,
                      profile_types: form.profile_types.includes(tp)
                        ? form.profile_types.filter((x) => x !== tp)
                        : [...form.profile_types, tp],
                    })
                  }
                >
                  {t(`profile_types.${tp}`, tp)}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <button type="submit" className="vb-btn vb-btn-primary" data-testid="admin-people-create-submit">
              Créer
            </button>
          </div>
        </form>
      )}

      <div className="mt-8 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="admin-people-table">
          <thead>
            <tr className="text-left vb-label border-b border-white/10">
              <th className="py-3 pr-4">Utilisateur</th>
              <th className="py-3 pr-4">Rôle système</th>
              <th className="py-3 pr-4">Types de profil</th>
              <th className="py-3 pr-4">Actif</th>
              <th className="py-3" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-white/5" data-testid={`admin-people-row-${u.id}`}>
                <td className="py-3 pr-4">
                  {u.display_name}
                  <div className="text-xs text-neutral-600">{u.email}</div>
                </td>
                <td className="py-3 pr-4">
                  <select
                    className="vb-input !py-1 !text-xs"
                    value={u.security_role}
                    disabled={me?.security_role !== "super_admin"}
                    data-testid={`admin-people-role-${u.id}`}
                    onChange={(e) => patch(u.id, { security_role: e.target.value })}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r} className="bg-black">
                        {r}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="py-3 pr-4">
                  <div className="flex flex-wrap gap-1 max-w-md">
                    {TYPES.map((tp) => {
                      const on = (u.profile_types || []).includes(tp);
                      return (
                        <button
                          key={tp}
                          className="vb-chip !py-0.5 !px-2 !text-[10px]"
                          data-active={on}
                          data-testid={`admin-people-${u.id}-type-${tp}`}
                          onClick={() =>
                            patch(u.id, {
                              profile_types: on
                                ? (u.profile_types || []).filter((x) => x !== tp)
                                : [...(u.profile_types || []), tp],
                            })
                          }
                        >
                          {t(`profile_types.${tp}`, tp)}
                        </button>
                      );
                    })}
                  </div>
                </td>
                <td className="py-3 pr-4">
                  <button
                    className="vb-chip !py-1"
                    data-active={u.is_active}
                    data-testid={`admin-people-active-${u.id}`}
                    onClick={() => patch(u.id, { is_active: !u.is_active })}
                  >
                    {u.is_active ? "Actif" : "Inactif"}
                  </button>
                </td>
                <td className="py-3">
                  {me?.security_role === "super_admin" && (
                    <button className="text-xs text-[#FF3B30] hover:underline" data-testid={`admin-people-delete-${u.id}`} onClick={() => remove(u.id)}>
                      Supprimer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
